/**
 * CourseVideoPlayer Component
 *
 * Modal video player for course modules using react-player.
 * Tracks and saves progress to backend.
 */

import { useQueryClient } from "@tanstack/react-query"
import { X } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import ReactPlayer from "react-player"
import { useTranslation } from "@/language"
import { updateVideoProgress } from "../services/ritual.service"
import type { CourseModule } from "../types/detail.types"

export interface CourseVideoPlayerProps {
  isOpen: boolean
  onClose: () => void
  module: CourseModule
  ritualId: string
}

// Progress tracking state
interface VideoProgress {
  currentTime: number
  duration: number
  percent: number
}

export function CourseVideoPlayer({ isOpen, onClose, module, ritualId }: CourseVideoPlayerProps) {
  const { t } = useTranslation("rituals")
  const queryClient = useQueryClient()

  // If module is already completed, start from 0 instead of saved position
  const isCompleted = module.progressStatus === "COMPLETED"
  const initialSeconds = isCompleted ? 0 : module.progressSeconds || 0

  // Track if we've already performed the initial seek
  const hasSoughtRef = useRef(false)

  const [progress, setProgress] = useState<VideoProgress>({
    currentTime: 0,
    duration: 0,
    percent: 0,
  })
  const [lastProgressLog, setLastProgressLog] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [_isBuffering, setIsBuffering] = useState(false)
  const playerRef = useRef<ReactPlayer | null>(null)

  // Reset and start playing when modal opens
  useEffect(() => {
    if (isOpen) {
      setHasError(false)
      // Reset the seek flag for new session
      hasSoughtRef.current = false
      // Reset progress state
      setProgress(prev => ({ ...prev, currentTime: 0, percent: 0 }))
      // Don't auto-play - let onReady handle seeking first, then play
      setPlaying(false)
    } else {
      setPlaying(false)
      setIsBuffering(false)
    }
  }, [isOpen])

  // Handle video start
  const handleStart = useCallback(() => {
    // Video started playing
  }, [])

  // Handle video pause
  const handlePause = useCallback(() => {
    // Video paused
  }, [])

  // Handle video end
  const handleEnded = useCallback(() => {
    // Video completed
  }, [])

  // Handle progress updates
  const handleProgress = useCallback(
    (state: { played: number; playedSeconds: number; loaded: number; loadedSeconds: number }) => {
      const newProgress: VideoProgress = {
        currentTime: state.playedSeconds,
        duration: state.loadedSeconds || progress.duration,
        percent: state.played * 100,
      }

      setProgress(newProgress)

      // Track first progress update to avoid spam
      if (lastProgressLog === 0) {
        setLastProgressLog(state.playedSeconds)
      }
    },
    [lastProgressLog, progress.duration],
  )

  // Handle duration ready
  const handleDuration = useCallback((duration: number) => {
    setProgress(prev => ({ ...prev, duration }))
  }, [])

  // Handle video error
  const handleError = useCallback((error: unknown) => {
    console.error("[VideoPlayer] Error:", error)
    setHasError(true)
    setPlaying(false)
  }, [])

  // Handle buffer start
  const handleBuffer = useCallback(() => {
    setIsBuffering(true)
  }, [])

  // Handle buffer end
  const handleBufferEnd = useCallback(() => {
    setIsBuffering(false)
  }, [])

  // Handle close with save progress
  const handleClose = useCallback(async () => {
    // If module is already completed, don't track progress anymore
    const isAlreadyCompleted = module.progressStatus === "COMPLETED"

    if (isAlreadyCompleted) {
      onClose()
      return
    }

    // Get seconds watched - use Math.round to preserve accuracy
    const secondsWatched = Math.round(progress.currentTime)

    // Determine status based on percentage watched
    // Mark as COMPLETED if user watched 95% or more
    const status = progress.percent >= 95 ? "COMPLETED" : "IN_PROGRESS"

    try {
      // Save progress to backend (sending seconds directly)
      await updateVideoProgress(ritualId, {
        moduleId: module.id,
        secondsWatched,
        status,
        ...(status === "COMPLETED" ? { completedAt: new Date().toISOString() } : {}),
      })

      // Refetch the ritual data to get updated progress
      await queryClient.refetchQueries({ queryKey: ["ritual", ritualId] })
    } catch (error) {
      console.error("[VideoPlayer] Failed to save progress:", error)
    }

    onClose()
  }, [module.progressStatus, module.id, ritualId, progress, onClose, queryClient])

  // Handle ready event - video is ready to play
  const handleReady = useCallback(() => {
    const duration = playerRef.current?.getDuration()

    // Update duration when ready
    if (playerRef.current) {
      setProgress(prev => ({ ...prev, duration: duration || 0 }))
    }

    // If completed or no saved position, start from beginning
    if (initialSeconds === 0) {
      setPlaying(true)
      return
    }

    // Only seek once, and only if we have a saved position
    if (!hasSoughtRef.current && initialSeconds > 0 && playerRef.current) {
      // Seek to the saved position (using 'seconds' as the unit)
      playerRef.current.seekTo(initialSeconds, "seconds")
      hasSoughtRef.current = true

      // Small delay to ensure seek completes before playing
      setTimeout(() => {
        setPlaying(true)
      }, 200)
    }
  }, [initialSeconds])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgb(0,0,0,0.7)] backdrop-blur-sm p-4 mb-0">
      <div className="relative w-full max-w-5xl bg-background rounded-lg overflow-hidden shadow-2xl">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors"
          aria-label={t("videoPlayer.close")}
        >
          <X size={20} />
        </button>

        {/* Video Player */}
        <div className="aspect-video bg-black">
          {hasError ? (
            <div className="w-full h-full flex items-center justify-center text-white p-8">
              <div className="text-center">
                <p className="text-lg font-semibold mb-2">{t("videoPlayer.error")}</p>
                <p className="text-sm opacity-75">
                  Please check your internet connection or try again later
                </p>
              </div>
            </div>
          ) : (
            <ReactPlayer
              ref={playerRef}
              url={module.videoUrl || ""}
              width="100%"
              height="100%"
              playing={playing}
              controls
              onStart={handleStart}
              onPause={handlePause}
              onEnded={handleEnded}
              onProgress={handleProgress}
              onDuration={handleDuration}
              onReady={handleReady}
              onError={handleError}
              onBuffer={handleBuffer}
              onBufferEnd={handleBufferEnd}
              progressInterval={1000}
            />
          )}
        </div>
      </div>
    </div>
  )
}
